import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Container } from 'Components/Container';
import React, { useState, useEffect  } from 'react';
import { Card } from '../../../../Components/Card';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useAuthTenant } from '../../../../store/auth';
import {
  MenuItem, FormControl, Select, InputLabel, Box, Typography,
  Button, CircularProgress, FormHelperText,
} from '@mui/material';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { getGapAnalysisControl, postGapAnalysisVinculo } from 'services/gap-analysis';
import ItemSelector from '../../../../Components/ItemSelector';
import { Column, Row } from '../../../../Components/Grid';
import { Title } from '../ControlDetal/styles';
import { options, getSolutionKey, getBreadcrumbBase, translateDomain, translateFunctionGrouping, translateApplicability } from '../../../../utils/labels_utils'

export const GapAnalysisControlLinkShow = () => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const selectedTenant = useSelector(useAuthTenant);
  const tenant = selectedTenant.slug;
  const [searchTerm, setSearchTerm] = useState("");
  const [items, setItems] = useState([]);
  const { id, control_id } = useParams();
  const [selectedOption, setSelectedOption] = useState("company");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const breadcrumbBase = getBreadcrumbBase(location.pathname, t);

  const {
    isLoading,
    error,
    data: gapAnalysis,
  } = useQuery('getGapAnalysisControl', async () => {
    const data = await getGapAnalysisControl(id, control_id);
    return data.status === 200 ? data.data : null;
  });



  const {
    handleSubmit,
    formState: { errors },  setValue, trigger,  } = useForm({
    defaultValues: {
      company_id: selectedTenant._id || "",
      type: "companies",
      entity: [],
      controls: [],
    }
  });

  useEffect(() => {
    if (!selectedOption || selectedOption === "companies") {
      setItems(
        selectedTenant
          ? [{ _id: selectedTenant._id, name: selectedTenant.name, description: selectedTenant.description, type: 'companies' }]
          : []
      );
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const selectedObj = options.find((opt) => opt.value === selectedOption);

        if (!selectedObj || !selectedObj.fetchFunction) return;
        const response = await selectedObj.fetchFunction(tenant);
        const itemsWithType = response.data
          .map((item) => ({
            ...item,
            type: selectedObj.value,
          }))
          .filter(
            (item) =>
              !gapAnalysis.controlGapAnalysis.some(
                (control) =>
                  control.entity_id === item._id && control.entity_type === item.type
              )
          );
        setItems(itemsWithType || []);
      } catch (error) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedOption, tenant, selectedTenant, gapAnalysis]);

  const employeeSize = parseFloat(selectedTenant.size);
  const solutionKey = getSolutionKey(employeeSize);

  if (isLoading) return <p>{t('common:labels.loading')}</p>;
  if (error) return <p>{t('gap-analysis:controlsLink.error')}</p>;

  const submitVinculos = handleSubmit(async (data) => {
    try {
      const formattedData = {
        ...data,
        entity: data.entity.map((item) => ({
          id: item._id,
          type: item.type,
        })),
        controls: [{
          id: gapAnalysis.control._id,
          frameworksIds: gapAnalysis.frameworks.map((framework) => framework._id),
        }],
      };
      await postGapAnalysisVinculo(id, formattedData);
      navigate(`/gap-analysis/show/${id}`);
    } catch (error) {
      console.error("Error linking control:", error);
    }
  });




  return (
    <Container
      title={t('gap-analysis:controlLink.title', { control: gapAnalysis.control.scfControl })}
      breadcrumb={`${breadcrumbBase} / ${gapAnalysis.gapAnalysis?.name}`}
      linkPage={-1}
    >
      <Row className="px-2 mt-3 mb-3">
        <Column xs={12}>
          <Card>
            <Title className="m-0 mb-3">{t('gap-analysis:controlLink.detailsTitle')}</Title>
            <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
              <Typography><strong>{t('gap-analysis:controlLink.fields.domain')}</strong> {translateDomain(t, gapAnalysis.control.scfDomain)}</Typography>
              <Typography><strong>{t('gap-analysis:controlLink.fields.functionGroup')}</strong> <span>{translateFunctionGrouping(t, gapAnalysis.control.nistCsfFunctionGrouping)}</span></Typography>
              <Typography><strong>{t('gap-analysis:controlLink.fields.applicability')}</strong> {translateApplicability(t, gapAnalysis.control.applicabilityStatus)} </Typography>
              <Typography><strong>{t('gap-analysis:controlLink.fields.controlWeight')}</strong> {gapAnalysis.control.controlWeighting} </Typography>
            </div>
            <Typography>
              <strong>{t('gap-analysis:controlLink.fields.controlDesc')}</strong> {gapAnalysis.control.scfControlDescription}
            </Typography>
            <Typography style={{ marginTop: "16px" }}>
              <strong>{t('gap-analysis:controlLink.fields.requirements')}</strong>
            </Typography>
            {gapAnalysis.frameworks
              .filter(framework => gapAnalysis.control.frameworks[framework._id]?.length)
              .map(framework => (
                <Typography key={framework._id}>
                  {framework.sourceIdentifier}: {gapAnalysis.control.frameworks[framework._id].join(", ")}
                </Typography>
              ))}


            <Typography style={{ marginTop: "16px" }}>
              <strong>{t('gap-analysis:controlLink.fields.methods')}</strong>
              <Typography style={{ whiteSpace: "pre-line" }}>
                {gapAnalysis.control[solutionKey]}
              </Typography>
            </Typography>

          </Card>
        </Column>
      </Row>
      <Row className="px-2 mt-3 mb-3">
        <Column xs={12}>
          <Card sx={'mt-4'}>
            <Title className="m-0 mb-3">{t('gap-analysis:controlLink.linkElementsTitle')}</Title>
            <Box display="flex" flexDirection="column" gap={3} padding={2}>
              <FormControl fullWidth>
                <InputLabel id="select-label">{t('gap-analysis:controlsLink.elementsLabel')}</InputLabel>
                <Select
                  labelId="select-label"  notched label={t('gap-analysis:controlsLink.elementsLabel')}
                  value={selectedOption}
                  variant="outlined"
                  onChange={(e) => setSelectedOption(e.target.value)}
                >
                  {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {loading && <CircularProgress sx={{ mt: 1 }} />}
              <FormControl fullWidth margin="normal" error={!!errors.entity}>
                <ItemSelector
                  config={{
                    title: t('common:itemSelector.itemNounTitle'),
                    idKey: "_id",
                    nameKey: "name",
                    descriptionKey: "description",
                    formKey: "entity",
                  }}
                  items={items}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  setValue={setValue}
                  trigger={trigger}
                />
                {errors.entity && <FormHelperText>{errors.entity.message}</FormHelperText>}

              </FormControl>
              <Button variant="contained" color="primary" onClick={submitVinculos}>
                {t('gap-analysis:controlLink.saveButton')}
              </Button>
            </Box>
          </Card>
        </Column>
      </Row>
    </Container>
  )
}
